(ns hypnos.dme.tile.demographics
  "Demographics tile."
  (:require [hypnos.dme.core.auth :as auth]
            [hypnos.dme.core.patient :as core-patient]
            [hypnos.dme.subs]
            [hypnos.dme.tile.field.patient-communication-preferences :as patient-communication-preferences]
            [hypnos.dme.tile.field.patient-contact :as patient-contact]
            ;; [hypnos.dme.tile.field.patient-dob :as patient-dob]
            [hypnos.dme.tile.field.patient-gender :as patient-gender]
            [hypnos.dme.tile.field.patient-image :as patient-image]
            [hypnos.dme.tile.field.patient-key :as patient-key]
            [hypnos.dme.tile.field.patient-name :as patient-name]
            [hypnos.dme.tile.field.patient-timezone :as patient-timezone]
            [hypnos.graphql.mutations :as mutations]
            [hypnos.i18n :refer [tr]]
            [hypnos.translator.patient :as t-p]
            [re-frame.core :as rf]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- default-patient-timezone
  [state org]
  (when-not (get-in @state [:patient :patient/timezone :value])
    (swap! state
           assoc-in [:patient :patient/timezone :value]
           (:organization/timezone org))))

(defn- load
  [body]
  (if-let [_loading? @(rf/subscribe [:dme/gql-request-in-progress?])]
    [:div.card-content
     [:section.section
      [:div.content.has-text-grey.has-text-centered
       [:p
        [:span.icon.is-large
         [:i.mdi.mdi-loading.mdi-48px.mdi-spin]]]
       [:p (tr :ui.label/loading)]]]]
    body))

(defn- consult-contains-answers?
  "Returns true if the consult contains any answers."
  [consult]
  (or (not (nil? (get-in consult [:consult/weight :value])))
      (not (nil? (get-in consult [:consult/take-dentures-out? :value])))
      (not (nil? (get-in consult [:consult/sleep-motion? :value])))
      (not (nil? (get-in consult [:consult/mouth-breathing-level :value])))
      (not (nil? (get-in consult [:consult/eye-dryness? :value])))
      (not (nil? (get-in consult [:consult/claustrophobia-level :value])))
      (not (nil? (get-in consult [:consult/chronic-issues? :value])))
      (not (nil? (get-in consult [:consult/deviated-septum? :value])))
      (not (nil? (get-in consult [:consult/pacemaker? :value])))
      (not (nil? (seq (get-in consult [:consult/issues :value]))))
      (not (nil? (seq (get-in consult [:consult/sleep-positions :value]))))))

(defn- edit-demographics?
  "Returns true if the patient demographics can be edited."
  [state]
  (or (and (:patient-looked-up? state)
           (:edit-patient? state)
           (get-in state [:patient :patient/id :value]))
      (and (:patient-looked-up? state)
           (nil? (get-in state [:patient :patient/id :value])))))

(defn- edit-patient-key?
  "Returns true if the patient key can be edited."
  [state]
  (and (:new-consult? state)
       (not (:patient-looked-up? state))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Tile

(defn tile
  [state]
  (let [auth               (rf/subscribe [:dme/auth])
        organization       (rf/subscribe [:dme/organization])
        _                  (default-patient-timezone state @organization)
        patient            (rf/subscribe [:dme/patient])
        new-patient?       (core-patient/new-patient? state)
        edit-patient?      (:edit-patient? @state)
        edit-patient-key?  (edit-patient-key? @state)
        edit-demographics? (edit-demographics? @state)
        one-time-message?  (:one-time-message? @state)
        contains-answers?  (consult-contains-answers? (get-in @state [:patient :patient/last-consult]))]
    [:div.tile.is-ancestor
     ;; portrait
     [:div.tile.is-parent.is-4
      [:div.card.tile.is-child
       [:header.header.card-header
        [:p.card-header-title (tr :ui.label/portrait)]
        (when (and (:patient/id @patient)
                   (not (:patient/image @patient))
                   contains-answers?)
          [:div.buttons [patient-image/image-field]])]
       [:div.card-content
        [patient-image/image]
        [:hr]
        [patient-image/issues]]]]
     ;; demographics
     [:div.tile.is-parent
      [:div.card.tile.is-child
       [:header.header.card-header
        [:p.card-header-title (tr :ui.label/demographics)]
        ;; edit / save button
        (when (and (:patient-looked-up? @state)
                   (get-in @state [:patient :patient/id :value])
                   (auth/authorized? @auth :edit-patient))
          (when edit-patient?
            [:button.button.is-small.is-success
             {:on-click (fn [_]
                          (let [org     (dissoc @organization :user/_organizations)
                                patient (-> (t-p/patient->db @state)
                                            (dissoc :patient/image
                                                    :patient/mask
                                                    :patient/selector
                                                    :patient/selector-type
                                                    :patient/anatomy
                                                    :patient/recommendations
                                                    :patient/last-consult
                                                    :patient/organization
                                                    :consult/_patient
                                                    :communication/_recipient)
                                            (assoc :patient/organization org))]
                            ;; submit information
                            (rf/dispatch-sync [:amplify/mutation
                                               mutations/store-patient
                                               {:patient (t-p/patient-with-consults->gql patient)}
                                               :dme/set-patient
                                               :dme/set-error])
                            (swap! state assoc :edit-patient? false)))}
             [:span.icon.is-small [:i.mdi.mdi-check]]
             [:span (tr :ui.label/save)]]))
        (when (and (:patient-looked-up? @state)
                   (get-in @state [:patient :patient/id :value])
                   (auth/authorized? @auth :edit-patient))
          [:button.button.is-small
           {:class    (if edit-patient? "is-warning" "is-success is-light")
            :on-click (fn [_] (swap! state assoc :edit-patient? (not edit-patient?)))}
           [:span.icon.is-small
            (if edit-patient?
              [:i.mdi.mdi-cancel]
              [:i.mdi.mdi-pencil])]
           [:span (if edit-patient?
                    (tr :ui.label/cancel)
                    (tr :ui.label/edit))]])
        ;; one-time message button
        (when (and (auth/authorized? @auth :send-message)
                   (or (contains? #{:consult-state/new :consult-state/in-progress}
                                  (get-in @state [:patient :patient/last-consult :consult/status :value]))
                       (and (= :consult-state/pending (get-in @state [:patient :patient/last-consult :consult/status :value]))
                            (nil? (:patient/image @patient)))))
          [:button.button.is-small.is-success.is-light
           {:on-click (fn [_] (swap! state assoc :one-time-message? (not one-time-message?)))}
           [:span.icon.is-small [:i.mdi.mdi-message-alert]]
           [:span (tr :ui.label/message)]
           [:span.icon.is-small
            (if one-time-message?
              [:i.mdi.mdi-chevron-down]
              [:i.mdi.mdi-chevron-up])]])
        ;; new consult button
        (when (and (auth/authorized? @auth :create-new-consult)
                   (not new-patient?)
                   (not (:new-consult? @state))
                   (contains? #{:consult-state/completed :consult-state/canceled :consult-state/expired}
                              (get-in @state [:patient :patient/last-consult :consult/status :value])))
          [:button.button.is-success.is-small
           {:on-click (fn [_]
                        (swap! state assoc :new-consult? true)
                        (core-patient/setup-new-consult state @patient))}
           [:span (tr :ui.label/new) " " (tr :ui.label/consult)]])]
       (load
        [:div.card-content
         (when one-time-message?
           [:div
            [patient-contact/one-time-contact-field @patient]
            [:hr]])
         ;; patient info
         [patient-key/field state {:disabled (not edit-patient-key?)}]
         [patient-name/field state {:disabled (not edit-demographics?)}]
         ;; [patient-dob/field state {:disabled (not edit-demographics?)}]
         [patient-gender/field state {:disabled (not edit-demographics?)}]
         [patient-contact/contact-field state {:disabled (not edit-demographics?)}]
         [patient-contact/alt-contact-field state {:disabled (not edit-demographics?)}]
         [patient-communication-preferences/field state {:disabled (not edit-demographics?)}]
         [patient-timezone/field state {:disabled (not edit-demographics?)}]])]]]))
